//* Libraries imports
import { motion } from 'framer-motion';
import { Row, Col } from 'react-bootstrap';

//? Styles imports
import './process-edital-skeleton.scss';

interface ProcessEditalSkeletonProps {
  showContent?: boolean;
}

/**
 * ProcessEditalSkeleton Component
 * 
 * Provides a skeleton loading state for the ProcessEditalView component.
 * Mimics the layout structure of the edital view with animated placeholders.
 * 
 * @param showContent - Whether to show the content skeleton (default: true)
 */
export function ProcessEditalSkeleton({ showContent = true }: ProcessEditalSkeletonProps) {
  const skeletonVariants = {
    pulse: {
      opacity: [0.6, 1, 0.6],
      transition: {
        duration: 1.5,
        repeat: Infinity
      }
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        duration: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4 }
    }
  };

  return (
    <motion.div
      className="process-edital-skeleton"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >      {/* Header Section Skeleton */}
      <motion.section 
        className="exam-header-skeleton p-4"
        variants={itemVariants}
      >
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            {/* Back Button Skeleton */}
            <motion.div
              className="skeleton-back-button me-3"
              variants={skeletonVariants}
              animate="pulse"
            />
            <div>
              {/* Process Title Skeleton */}
              <motion.div
                className="skeleton-process-title mb-2"
                variants={skeletonVariants}
                animate="pulse"
              />
              {/* Process Info Skeleton */}
              <div className="d-flex gap-3">
                <motion.div
                  className="skeleton-process-info"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-process-info"
                  variants={skeletonVariants}
                  animate="pulse"
                />
                <motion.div
                  className="skeleton-process-info"
                  variants={skeletonVariants}
                  animate="pulse"
                />
              </div>
            </div>
          </div>
          {/* Status Badge Skeleton */}
          <motion.div
            className="skeleton-status-badge"
            variants={skeletonVariants}
            animate="pulse"
          />
        </div>
        
        {/* Process Info Card Skeleton */}
        <motion.div
          className="skeleton-info-card"
          variants={skeletonVariants}
          animate="pulse"
        />
      </motion.section>

      {/* Content Skeleton */}
      {showContent && (
        <motion.div
          className="container-fluid px-3 py-4"
          variants={itemVariants}
        >
          <Row className="justify-content-center">
            <Col xs={12} lg={10} xl={8}>
              <motion.div
                className="skeleton-edital-content-card"
                variants={itemVariants}
              >
                {/* Card Header Skeleton */}
                <div className="skeleton-card-header">
                  <div className="d-flex align-items-center">
                    <motion.div
                      className="skeleton-header-icon me-2"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                    <motion.div
                      className="skeleton-header-title"
                      variants={skeletonVariants}
                      animate="pulse"
                    />
                  </div>
                </div>

                {/* Card Body Skeleton */}
                <div className="skeleton-card-body-content">
                  {/* Info Section */}
                  <div className="skeleton-info-section mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <motion.div
                        className="skeleton-info-icon-small me-2"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                      <motion.div
                        className="skeleton-info-text"
                        variants={skeletonVariants}
                        animate="pulse"
                      />
                    </div>
                  </div>

                  {/* Content Lines Skeleton */}
                  <div className="skeleton-content-lines">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((index) => (
                      <motion.div
                        key={index}
                        className={`skeleton-content-line ${
                          index % 4 === 0 ? "short" : ""
                        }`}
                        variants={skeletonVariants}
                        animate="pulse"
                        style={{ animationDelay: `${index * 0.1}s` }}
                      />
                    ))}
                  </div>

                  {/* Paragraph Breaks */}
                  <div className="skeleton-paragraph-break" />

                  {/* More Content Lines */}
                  <div className="skeleton-content-lines">
                    {[1, 2, 3, 4, 5, 6].map((index) => (
                      <motion.div
                        key={index + 10}
                        className={`skeleton-content-line ${
                          index % 3 === 0 ? "medium" : ""
                        }`}
                        variants={skeletonVariants}
                        animate="pulse"
                        style={{ animationDelay: `${(index + 10) * 0.1}s` }}
                      />
                    ))}
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </motion.div>
      )}
    </motion.div>
  );
}
